import { useState, useEffect } from "react";
import { getAuthHeader } from "../lib/auth";

export interface WorkspaceMember {
  user_id: string;
  email: string;
  display_name?: string;
  workspace_id: string;
  role: "human_lead" | "human_partner";
  joined_at: Date;
  invited_by?: string;
}

export interface WorkspaceMembersValue {
  members: WorkspaceMember[];
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

export function useWorkspaceMembers(workspaceId: string): WorkspaceMembersValue {
  const [members, setMembers] = useState<WorkspaceMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Extract fetchMembers to make it reusable for both initial load and refetch
  const fetchMembers = async () => {
    if (!workspaceId) {
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`/api/workspace/members?workspaceId=${encodeURIComponent(workspaceId)}`, {
        headers: await getAuthHeader(),
      });

      if (!response.ok) {
        throw new Error(`API request failed: ${response.statusText}`);
      }

      const data = await response.json();
      const apiMembers = data.members || [];

      // Transform API response to match WorkspaceMember interface
      const transformedMembers: WorkspaceMember[] = apiMembers.map((m: any) => ({
        user_id: m.uid || '',
        email: m.email || '',
        display_name: m.display_name,
        workspace_id: workspaceId,
        role: m.role || 'human_partner',
        joined_at: new Date(m.joined_at),
        invited_by: undefined, // Not provided by API
      }));

      setMembers(transformedMembers);
      setLoading(false);
    } catch (err) {
      console.error('[useWorkspaceMembers] Error:', err);
      setError(err instanceof Error ? err : new Error(String(err)));
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMembers();
  }, [workspaceId]);

  const refetch = async () => {
    // Directly call fetchMembers and return the promise
    await fetchMembers();
  };

  return { members, loading, error, refetch };
}
